import { memo } from 'react';

import cn from 'classnames';
import Lottie from 'lottie-react';

import { MagicPen } from '../MagicPen';
import { BottomSheetDrawer } from '../BottomSheetDrawer';
import { AvatarDisplay, BoxFooter, VideoDisplay, VoiceIndicator } from './partials';

import { isValidVideoStream } from '@src/helpers';

import useVideoBox from './useVideoBox';

import PipIcon from '@src/assets/icons/PipIcon';

import { IVideoStream, TVideoObjectFit } from '@src/types';

import styles from './videoBox.module.scss';

interface IProps extends IVideoStream {
  style?: React.CSSProperties;
  showName?: boolean;
  isPipBox?: boolean;
  displaySmallAvatar?: boolean;
  onVideoClick?: () => void;
  isWbActive?: boolean;
  objectFit?: TVideoObjectFit;
  isBigScreen?: boolean;
}

const VideoBox = ({
  style,
  onVideoClick,
  displaySmallAvatar,
  isWbActive,
  isPipBox = false,
  showName = true,
  objectFit,
  isBigScreen,
  ...videoStream
}: IProps) => {
  const {
    videoRef,
    wavyScale,
    peerEmoji,
    isMicMuted,
    audioVolumeTmp,
    isPeerTalking,
    handRaiseOrder,
    isMenuPopperOpened,
    hoveredVideoBox,
    selectedVideoObjectFit,
    setIsMenuPopperOpened,

    // context menu
    canvasRef,
    isMirrorActive,
    whiteboardSettings,

    pip,
    peerColor,
    borderColor,
    backgroundColor,
    animations,
    isHandRaised,
    containerRef,
    isMobileDevice,
    isDrawerOpen,
    shouldShowMagicPen,
    canAnnotate,

    videoDimensions,

    handleMouseEnter,
    handleMouseLeave,
    videoActions,
    openDrawer,
    handleDrawerToggle,
    stream: activeStream,
  } = useVideoBox(videoStream, isPipBox, objectFit);

  const { id, muted, poster, peerName, isCurrentUser, peerId } = videoStream;
  const hasValidVideo = activeStream && isValidVideoStream(activeStream);
  const isScreenShare = videoStream.type === 'screenType';

  return (
    <div
      ref={containerRef}
      className={cn(styles.videoScreenContainer, {
        [styles.videoScreenContainerMobile]: isMobileDevice,
        [styles.boxBorder]: isPeerTalking,
        [styles.screenShare]: isScreenShare,
      })}
      style={{
        ...(style && { ...style }),
        ...(!hasValidVideo && isBigScreen && !isScreenShare && { width: '100%' }),
        // Apply peer color as background for both video and avatar states
        backgroundColor:
          shouldShowMagicPen || hasValidVideo
            ? 'transparent'
            : isPipBox
              ? 'var(--pip-bg-color)'
              : (backgroundColor ?? 'hsla(0, 0%, 50%, 1)'),
        ...(isPeerTalking && { border: `3px solid ${borderColor ?? 'rgb(13, 220, 98)'}` }),
      }}
      role="button"
      tabIndex={0}
      onMouseEnter={() => handleMouseEnter()}
      onMouseLeave={() => handleMouseLeave()}
      onClick={() => {
        onVideoClick?.();
      }}
    >
      {hasValidVideo ? (
        <>
          <VideoDisplay
            videoRef={videoRef}
            isMirrorActive={isMirrorActive}
            muted={muted}
            poster={poster}
            objectFit={objectFit || selectedVideoObjectFit}
            id={id}
          />
          {pip.isActive && pip.activeId === videoRef.current?.id && (
            <div
              className={cn(styles.customPipOverlay, {
                '!pt-6': isMobileDevice,
                '!justify-start !pt-2': style?.width === '272px',
              })}
            >
              <PipIcon
                width={style?.width === '272px' ? 80 : isMobileDevice ? 40 : 109}
                height={isMobileDevice ? 40 : 89}
              />
              <p
                className={cn({
                  '!mt-2 !text-[0.7rem] whitespace-normal': isMobileDevice,
                  '!text-[0.9rem] !my-0': style?.width === '272px',
                })}
              >
                {isMobileDevice && isCurrentUser ? 'PIP' : 'This video is playing in Picture-in-Picture mode.'}
              </p>
            </div>
          )}
        </>
      ) : (
        <AvatarDisplay peerId={peerId} peerName={peerName} audioVolumeTmp={audioVolumeTmp} />
      )}
      <VoiceIndicator
        isMicMuted={isMicMuted}
        wavyScale={wavyScale}
        peerId={peerId}
        top={`${(isScreenShare ? (videoDimensions.offsetY ?? 0) : 0) + 6}px`}
        isPipBox={isPipBox}
      />
      {/* Emoji animation at the container level, not in the footer */}
      {!!peerEmoji && animations[peerEmoji] && (
        <div className={cn(styles.videoEmojiIcon, styles.pulseIcon)}>
          <Lottie
            id={`video-box-emoji_${peerEmoji}`}
            animationData={animations[peerEmoji]}
            style={{ width: 41, height: 54 }}
          />
        </div>
      )}

      {/* Magic Pen - Only show for screen shares: if current user is sharing OR if host is viewing someone's screen share */}
      {shouldShowMagicPen && (
        <MagicPen videoRef={videoRef} videoDimensions={videoDimensions} isReadMode={!isBigScreen} />
      )}
      {!isScreenShare && !(canAnnotate && shouldShowMagicPen) && (
        <BoxFooter
          isHandRaised={isHandRaised}
          handRaiseOrder={handRaiseOrder}
          peerName={peerName}
          peerColor={peerColor}
          isCurrentUser={!!isCurrentUser}
          showName={showName}
          isPictureInPictureActive={pip.isActive}
          animations={animations}
          peerEmoji={peerEmoji}
          actionMenuProps={{
            isMobile: isMobileDevice,
            isDrawerOpen,
            isMenuPopperOpened,
            setIsMenuPopperOpened,
            hoveredVideoBox,
            whiteboardSettings,
            handleDrawerToggle,
            videoActions,
          }}
        />
      )}

      <canvas ref={canvasRef} className={styles.hidden} />
      <BottomSheetDrawer
        title={peerName}
        open={isMobileDevice && openDrawer}
        onClose={handleDrawerToggle}
        actions={videoActions}
      />
    </div>
  );
};

export default memo(VideoBox);
